import React, { useEffect, useState, useCallback } from 'react';
import * as translator from '../../../../../utils/translator';
import * as http from '../../../../../utils/http';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
// Standalone brand logos (src/icons/*.svg are combined "accepted cards" graphics — unusable
// as per-brand icons). This is the same clean asset set SubscriptionRootCategoryCard uses.
import VisaIcon from '../../../../../images/Payment Methods/visa.png';
import MastercardIcon from '../../../../../images/Payment Methods/mastercard.png';
import MaestroIcon from '../../../../../images/Payment Methods/maestro-2-logo-png-transparent 1.png';
import PayPalIcon from '../../../../../images/Payment Methods/de_paypal.png';
import KlarnaIcon from '../../../../../images/Payment Methods/klarna_dark.png';
import GenericCardIcon from '../../../../../icons/careplus-detail-card.svg';
import {
  ChangePaymentForm,
  Session,
  SESSION_ENDPOINT,
  MIDDLEWARES,
  getStripe,
  appearance,
} from './CarePlusChangePaymentForm';
import './CarePlusStripePaymentMethod.css';
import './CarePlusChangePaymentForm.css';

interface CarePlusStripePaymentMethodProps {
  subscriptionID: number;
  mode: 'view' | 'recovery';
  language: string;
  locale: string;
  onChanged: () => void;
  setStickyNotificationDetails: (details: any) => void;
  hideChangeButton?: boolean;
}

// Read-only display of the subscription's saved method (mirrors backend SavedPaymentMethod).
interface SavedMethod {
  type: string;
  brand?: string;
  last4?: string;
  expMonth?: number;
  expYear?: number;
  email?: string;
  bankName?: string;
  label?: string;
}

const CURRENT_ENDPOINT = 'account-update/subscription/care-plus/payment-method/current';

// Icon that matches the actual brand the customer used. Brand strings from Stripe are
// lowercase (e.g. "visa", "mastercard"). Extend this map (asset + one line) as we add
// support for more networks; any brand without dedicated art falls back to a generic
// credit-card glyph, so a card is never iconless and never shows the wrong logo.
const CARD_BRAND_ICONS: Record<string, string> = {
  visa: VisaIcon,
  mastercard: MastercardIcon,
  maestro: MaestroIcon,
};

const iconFor = (method: SavedMethod): string => {
  if (method.type.includes('paypal')) return PayPalIcon;
  if (method.type.includes('klarna')) return KlarnaIcon;
  return CARD_BRAND_ICONS[(method.brand || '').toLowerCase()] || GenericCardIcon;
};

// Human-readable brand name for known brands; falls back to capitalising the raw value.
const CARD_BRAND_NAMES: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  maestro: 'Maestro',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');

const brandName = (brand?: string) => {
  const key = (brand || '').toLowerCase();
  return CARD_BRAND_NAMES[key] || (brand ? capitalize(brand) : 'Card');
};

const formatExpiry = (month?: number, year?: number) =>
  month && year ? `${String(month).padStart(2, '0')}/${String(year).slice(-2)}` : '';

// ============================================================================
// Widget — shows the saved method read-only; expands the Stripe form inline
// ============================================================================

const CarePlusStripePaymentMethod: React.FC<CarePlusStripePaymentMethodProps> = ({ subscriptionID, mode, language, locale, onChanged, setStickyNotificationDetails, hideChangeButton }) => {
  const [savedMethod, setSavedMethod] = useState<SavedMethod | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [session, setSession] = useState<Session | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);

  const t = (path: string) => translator.translate(language, 'my_account', path);

  // Load the current saved method for read-only display.
  const fetchSavedMethod = useCallback(() => {
    http.getFromServer(`${CURRENT_ENDPOINT}?subscriptionID=${subscriptionID}`, MIDDLEWARES)
      .then((res: any) => {
        const data = res?.data ?? res;
        if (data?.method) {
          setSavedMethod(data.method);
        }
      })
      .catch(() => {});
  }, [subscriptionID]);

  useEffect(() => { fetchSavedMethod(); }, [fetchSavedMethod]);

  const handleSaved = useCallback(() => {
    onChanged();
    setShowForm(false);
    fetchSavedMethod();
  }, [onChanged, fetchSavedMethod]);

  const closeForm = useCallback(() => setShowForm(false), []);

  // Fetch the Stripe CustomerSession on first open, then reuse it.
  const handleChangeClick = useCallback(() => {
    setShowForm(true);
    if (!session) {
      setSessionLoading(true);
      http.postToServer(SESSION_ENDPOINT, { subscriptionID }, MIDDLEWARES)
        .then((res: any) => {
          const data = res?.data ?? res;
          if (data?.customerSessionSecret) {
            setSession({
              customerSessionSecret: data.customerSessionSecret,
              publicKey: data.publicKey,
              paymentMethods: data.paymentMethods ?? [],
              currency: data.currency,
            });
          }
        })
        .catch(() => {
          setShowForm(false);
          setStickyNotificationDetails({
            show: true,
            level: 'error',
            title: t('CarePlus/paymentMethod/errorTitle'),
            content: t('CarePlus/paymentMethod/errorBody'),
          });
        })
        .finally(() => setSessionLoading(false));
    }
  }, [session, subscriptionID, setStickyNotificationDetails, language]);

  const orderedMethods = session
    ? [...session.paymentMethods].sort((a, b) => (a === 'card' ? -1 : b === 'card' ? 1 : 0))
    : [];

  const stripeOptions: StripeElementsOptions | null = session ? {
    mode: 'setup',
    currency: session.currency,
    paymentMethodCreation: 'manual',
    paymentMethodTypes: orderedMethods,
    customerSessionClientSecret: session.customerSessionSecret,
    appearance,
    locale: language,
  } as StripeElementsOptions : null;

  // Two-line display: provider/brand name on top, method details below.
  const renderMethodText = (method: SavedMethod) => {
    if (method.type.includes('paypal')) {
      return (
        <>
          <span className="careplus-payment-method__primary">PayPal</span>
          {method.email && <span className="careplus-payment-method__secondary">{method.email}</span>}
        </>
      );
    }
    if (method.type.includes('klarna')) {
      return <span className="careplus-payment-method__primary">Klarna</span>;
    }
    const primary = method.last4
      ? `${brandName(method.brand)} •••• ${method.last4}`
      : brandName(method.brand);
    const expiry = formatExpiry(method.expMonth, method.expYear);
    return (
      <>
        <span className="careplus-payment-method__primary">{primary}</span>
        {expiry && (
          <span className="careplus-payment-method__secondary">
            {t('CarePlus/paymentMethod/expires').replace(':date', expiry)}
          </span>
        )}
      </>
    );
  };

  return (
    <div className={`careplus-payment-method careplus-payment-method--${mode}`}>
      <div className="careplus-payment-method__summary">
        {savedMethod ? (
          <>
            <img className="careplus-payment-method__brand" src={iconFor(savedMethod)} alt="" />
            <div className="careplus-payment-method__method">{renderMethodText(savedMethod)}</div>
          </>
        ) : (
          <>
            <img className="careplus-payment-method__brand" src={GenericCardIcon} alt="" />
            <div className="careplus-payment-method__method">
              <span className="careplus-payment-method__primary">{t('CarePlus/paymentMethod/noMethod')}</span>
            </div>
          </>
        )}
        {!showForm && !hideChangeButton && (
          <button
            type="button"
            className="careplus-payment-method__change-btn"
            onClick={handleChangeClick}
          >
            {t('CarePlus/detail/changePaymentMethod')}
          </button>
        )}
      </div>

      {showForm && (
        <div className="careplus-payment-method__form">
          {sessionLoading || !session || !stripeOptions ? (
            <div className="cppm-loading">
              <span className="cppm-spinner" role="progressbar" aria-label={t('CarePlus/paymentMethod/loading')} />
            </div>
          ) : (
            <Elements stripe={getStripe(session.publicKey)} options={stripeOptions}>
              <ChangePaymentForm
                subscriptionID={subscriptionID}
                mode={mode}
                language={language}
                onClose={closeForm}
                onSaved={handleSaved}
                setStickyNotificationDetails={setStickyNotificationDetails}
              />
            </Elements>
          )}
        </div>
      )}
    </div>
  );
};

export default CarePlusStripePaymentMethod;
